import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { formatDate, formatDuration, formatCurrency } from '../../utils/formatters';
import StatusBadge from '../../components/StatusBadge';
import {
  Clock,
  CheckCircle2,
  XCircle,
  Calendar,
  FileText,
  ChevronRight,
  Briefcase,
} from 'lucide-react';

const StatCard = ({ label, value, subValue, bgColor, iconColor, Icon }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-start gap-4">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${bgColor}`}>
      <Icon size={20} className={iconColor} strokeWidth={1.75} />
    </div>
    <div className="min-w-0">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5 leading-tight">{value}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="px-6 py-14 text-center text-gray-400">
    <FileText size={36} className="mx-auto mb-3 text-gray-300" strokeWidth={1.25} />
    <p className="text-sm">{message}</p>
  </div>
);

const AdminDashboard = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <StatCard
        label="Lançamentos Aprovados"
        value={data.stats?.approved_count || 0}
        subValue={`${data.stats?.approved_hours || '00:00'} horas`}
        Icon={CheckCircle2}
        bgColor="bg-green-50"
        iconColor="text-green-600"
      />
      <StatCard
        label="Lançamentos Pendentes"
        value={data.stats?.pending_count || 0}
        subValue={`${data.stats?.pending_hours || '00:00'} horas`}
        Icon={Clock}
        bgColor="bg-yellow-50"
        iconColor="text-yellow-600"
      />
      <StatCard
        label="Lançamentos Recusados"
        value={data.stats?.rejected_count || 0}
        subValue={`${data.stats?.rejected_hours || '00:00'} horas`}
        Icon={XCircle}
        bgColor="bg-red-50"
        iconColor="text-red-500"
      />
      <StatCard
        label="Valor Total Aprovado"
        value={formatCurrency(data.stats?.total_amount || 0)}
        Icon={Briefcase}
        bgColor="bg-blue-50"
        iconColor="text-blue-600"
      />
    </div>

    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">Lançamentos Recentes</h3>
        <span className="text-sm text-gray-500">
          {data.period?.month}/{data.period?.year}
        </span>
      </div>
      {data.recent_entries?.length === 0 ? (
        <EmptyState message="Nenhum lançamento registrado ainda." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Consultor</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Projeto</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Descrição</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Horas</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.recent_entries?.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-900">{entry.consultant_name}</td>
                  <td className="px-6 py-4 text-gray-600">{formatDate(entry.entry_date)}</td>
                  <td className="px-6 py-4 text-gray-600">{entry.client_name}</td>
                  <td className="px-6 py-4 text-gray-600">{entry.project_name}</td>
                  <td className="px-6 py-4 text-gray-600 max-w-xs truncate" title={entry.description || ''}>
                    {entry.description || '—'}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">{formatDuration(entry.total_hours)}</td>
                  <td className="px-6 py-4"><StatusBadge status={entry.approval_status} type="approval" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-6 py-4 border-t border-gray-100">
        <Link
          to="/approvals"
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
        >
          Ir para aprovação de horas
          <ChevronRight size={15} />
        </Link>
      </div>
    </div>
  </div>
);

const ConsultantDashboard = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <StatCard
        label="Horas Aprovadas"
        value={data.stats?.approved_hours || '00:00'}
        subValue={`${data.stats?.approved_count || 0} lançamento(s)`}
        Icon={CheckCircle2}
        bgColor="bg-green-50"
        iconColor="text-green-600"
      />
      <StatCard
        label="Horas Pendentes"
        value={data.stats?.pending_hours || '00:00'}
        subValue={`${data.stats?.pending_count || 0} lançamento(s)`}
        Icon={Clock}
        bgColor="bg-yellow-50"
        iconColor="text-yellow-600"
      />
      <StatCard
        label="Horas Recusadas"
        value={data.stats?.rejected_hours || '00:00'}
        subValue={`${data.stats?.rejected_count || 0} lançamento(s)`}
        Icon={XCircle}
        bgColor="bg-red-50"
        iconColor="text-red-500"
      />
    </div>

    {data.upcoming_entries?.length > 0 && (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <Calendar size={16} className="text-blue-600" />
          Próximos Lançamentos
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-blue-100">
              {data.upcoming_entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="py-2 text-blue-900 font-medium">{formatDate(entry.entry_date)}</td>
                  <td className="py-2 text-blue-700">{entry.project_name}</td>
                  <td className="py-2 text-blue-700 text-right">{formatDuration(entry.total_hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-800">Meus Lançamentos Recentes</h3>
      </div>
      {data.recent_entries?.length === 0 ? (
        <EmptyState message="Nenhum lançamento registrado ainda." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Projeto</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Descrição</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Horas</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.recent_entries?.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-gray-600">{formatDate(entry.entry_date)}</td>
                  <td className="px-6 py-4 text-gray-600">{entry.client_name}</td>
                  <td className="px-6 py-4 text-gray-900">{entry.project_name}</td>
                  <td className="px-6 py-4 text-gray-600 max-w-xs truncate" title={entry.description || ''}>
                    {entry.description || '—'}
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900">{formatDuration(entry.total_hours)}</td>
                  <td className="px-6 py-4"><StatusBadge status={entry.approval_status} type="approval" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-6 py-4 border-t border-gray-100">
        <Link
          to="/time-entries"
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
        >
          Ver todos os lançamentos
          <ChevronRight size={15} />
        </Link>
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { isStaff } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await api.get('/dashboard/');
        setData(response.data);
      } catch (err) {
        setError('Erro ao carregar dados do dashboard.');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-700 text-sm">{error}</p>
      </div>
    );
  }

  return isStaff ? <AdminDashboard data={data} /> : <ConsultantDashboard data={data} />;
};

export default Dashboard;
